import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/layout/PageHeader";
import { DIV } from "@/constants/testIds";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Building2, Plus, Upload, X } from "lucide-react";

const CURRENCIES = ["SGD", "MYR", "USD", "EUR", "GBP", "INR", "AUD", "IDR", "THB", "PHP", "JPY", "CNY", "HKD"];

export default function Divisions() {
  const { data: divs = [] } = useQuery({ queryKey: ["divs"], queryFn: () => api.get("/divisions").then((r) => r.data) });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  return (
    <div>
      <PageHeader
        overline="Company"
        title="Divisions"
        subtitle="Fully-isolated workspaces under one company. Each division has its own logo, accent colour, invoice header, and bank details."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid={DIV.createButton} className="rounded-sm bg-primary hover:bg-primary/90"><Plus className="h-4 w-4 mr-2" />Add Division</Button></DialogTrigger>
            <CreateDialog onClose={() => setOpen(false)} />
          </Dialog>
        }
      />
      <div className="px-6 lg:px-10 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {divs.map((d) => (
            <Card key={d.id} className="rounded-sm bg-card border-border p-6 group cursor-pointer" onClick={() => setEditing(d)} data-testid={`div-card-${d.id}`}>
              <div className="flex items-start gap-3 mb-3">
                <div
                  className="h-12 w-12 rounded-sm flex items-center justify-center overflow-hidden bg-white/5 border border-border"
                  style={{ borderColor: d.accent_color || undefined }}
                >
                  {d.logo_data_uri ? (
                    <img src={d.logo_data_uri} alt={d.name} className="h-full w-full object-contain p-1" data-testid={`div-logo-${d.id}`} />
                  ) : (
                    <Building2 className="h-5 w-5" style={{ color: d.accent_color || undefined }} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">{d.currency}</div>
                  <div className="font-display text-xl font-extrabold truncate">{d.name}</div>
                </div>
                <button onClick={(e) => { e.stopPropagation(); setEditing(d); }} className="opacity-0 group-hover:opacity-100 text-xs uppercase tracking-widest text-primary hover:underline" data-testid={`div-edit-${d.id}`}>Edit</button>
              </div>
              <div className="text-xs text-muted-foreground">Created {new Date(d.created_at).toLocaleDateString()}</div>
              {!d.logo_data_uri && (
                <div className="mt-3 text-[10px] uppercase tracking-widest text-amber-500/80 font-bold flex items-center gap-1">
                  <Upload className="h-2.5 w-2.5" /> Add a logo to brand this division
                </div>
              )}
            </Card>
          ))}
        </div>
      </div>
      {editing && <EditDialog division={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function EditDialog({ division, onClose }) {
  const [name, setName] = useState(division.name);
  const [currency, setCurrency] = useState(division.currency);
  const [logoDataUri, setLogoDataUri] = useState(division.logo_data_uri || null);
  const [accentColor, setAccentColor] = useState(division.accent_color || "#7C3AED");
  const qc = useQueryClient();

  const saveMut = useMutation({
    mutationFn: async () => {
      // 1. Save name + currency on the Division record
      await api.patch(`/divisions/${division.id}`, { name, currency });
      // 2. Save logo + accent on the division's billing profile
      const bpPayload = { logo_data_uri: logoDataUri, accent_color: accentColor };
      await api.put("/billing-profile", bpPayload, {
        params: { division_id: division.id },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["divs"] });
      qc.invalidateQueries({ queryKey: ["billing-profile"] });
      toast.success("Division updated");
      onClose();
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });

  const onLogoFile = (file) => {
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error("Please upload a logo under 500KB (crop or compress it).");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setLogoDataUri(reader.result);
    reader.readAsDataURL(file);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="div-edit-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl font-extrabold tracking-tight">Edit Division</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {/* Logo uploader */}
          <div>
            <Label>Logo</Label>
            <div className="mt-2 flex items-center gap-4">
              <div
                className="h-24 w-24 rounded-sm border-2 border-dashed flex items-center justify-center overflow-hidden bg-white/5"
                style={{ borderColor: accentColor }}
              >
                {logoDataUri ? (
                  <img src={logoDataUri} alt="Division logo" className="h-full w-full object-contain p-2" data-testid="div-edit-logo-preview" />
                ) : (
                  <Building2 className="h-8 w-8 text-muted-foreground" />
                )}
              </div>
              <div className="flex-1 space-y-2">
                <label className="block">
                  <div className="rounded-sm bg-primary/10 border border-primary/40 text-primary text-center py-2 text-sm font-semibold cursor-pointer hover:bg-primary/20 transition-colors">
                    <Upload className="h-3.5 w-3.5 mr-1.5 inline" />
                    {logoDataUri ? "Replace logo" : "Upload logo"}
                  </div>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml"
                    className="hidden"
                    data-testid="div-edit-logo-upload"
                    onChange={(e) => onLogoFile(e.target.files?.[0])}
                  />
                </label>
                {logoDataUri && (
                  <button
                    type="button"
                    onClick={() => setLogoDataUri(null)}
                    className="text-xs text-muted-foreground hover:text-red-400 w-full text-center flex items-center justify-center gap-1"
                    data-testid="div-edit-logo-remove"
                  >
                    <X className="h-3 w-3" /> Remove logo
                  </button>
                )}
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">PNG or JPEG under 500KB · 512×512 recommended</div>
              </div>
            </div>
          </div>

          {/* Accent color */}
          <div>
            <Label>Accent colour</Label>
            <div className="flex items-center gap-2 mt-1.5">
              <input
                type="color"
                value={accentColor}
                onChange={(e) => setAccentColor(e.target.value)}
                className="h-9 w-14 rounded-sm border border-border bg-secondary/40 cursor-pointer"
                data-testid="div-edit-accent"
              />
              <Input
                className="rounded-sm bg-secondary/40 flex-1"
                value={accentColor}
                onChange={(e) => setAccentColor(e.target.value)}
                placeholder="#7C3AED"
              />
            </div>
            <div className="mt-2 flex items-center gap-2">
              {["#7C3AED", "#0EA5E9", "#EF4444", "#10B981", "#F59E0B", "#0F172A"].map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setAccentColor(c)}
                  className="h-6 w-6 rounded-sm border-2 transition-colors"
                  style={{ background: c, borderColor: accentColor === c ? "#fff" : "transparent" }}
                  title={c}
                />
              ))}
            </div>
          </div>

          <div><Label>Name</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={name} onChange={(e) => setName(e.target.value)} data-testid="div-edit-name" /></div>
          <div>
            <Label>Currency</Label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger className="rounded-sm mt-1.5 bg-secondary/40" data-testid="div-edit-currency"><SelectValue /></SelectTrigger>
              <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button data-testid="div-edit-save" className="rounded-sm bg-primary" onClick={() => saveMut.mutate()} disabled={saveMut.isPending || !name}>
            {saveMut.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateDialog({ onClose }) {
  const [name, setName] = useState("");
  const [currency, setCurrency] = useState("SGD");
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => api.post("/divisions", { name, currency }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["divs"] }); toast.success("Division created"); onClose(); },
  });
  return (
    <DialogContent className="rounded-sm">
      <DialogHeader><DialogTitle className="font-display text-2xl font-extrabold tracking-tight">Create Division</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label>Name</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. APAC Division" /></div>
        <div>
          <Label>Currency</Label>
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger className="rounded-sm mt-1.5 bg-secondary/40"><SelectValue /></SelectTrigger>
            <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="text-[11px] text-muted-foreground italic">You can add a logo and brand accent after the division is created — just click the card.</div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
        <Button data-testid={DIV.createSubmit} className="rounded-sm bg-primary" onClick={() => mut.mutate()} disabled={mut.isPending || !name}>Create</Button>
      </DialogFooter>
    </DialogContent>
  );
}
